import { defineStore } from 'pinia';
import DocNetIssueService from 'src/services/docnet/DocNetIssueService';
import type {
  DocNetIssue,
  DocNetIssueCreate,
  DocNetIssueStatus,
  DocNetSubIssue,
  DocNetAgentRun,
  DocNetRunEvent,
} from 'src/@types/docnet';
import { useDocNetAgentStore } from './docnet-agents.store';
import { useDocNetMissionStore } from './docnet-missions.store';
import { useDocNetSettingsStore } from './docnet-settings.store';
import { mockIssues } from 'src/mocks/docnet/issues';

const issueService = new DocNetIssueService();

export interface DocNetIssueFilters {
  status: DocNetIssueStatus | '';
  priority: string;
  type: string;
  assignee_id: string;
  mission_id: string;
}

export const useDocNetIssueStore = defineStore('docnet_issues', {
  state: () => ({
    issues: [] as DocNetIssue[],
    myIssues: [] as DocNetIssue[],
    missionIssues: [] as DocNetIssue[],
    currentIssue: null as DocNetIssue | null,
    loading: false,
    filters: {
      status: '',
      priority: '',
      type: '',
      assignee_id: '',
      mission_id: '',
    } as DocNetIssueFilters,
    runs: [] as DocNetAgentRun[],
    activeRunId: null as string | null,
    // Server-side pagination
    page: 1,
    pageSize: 20,
    totalIssues: 0,
    // Server-side search — trimmed, case-insensitive match against
    // title + description (backend `q` param). Component debounces input.
    searchFilter: '' as string,
  }),

  getters: {
    issuesByStatus:
      (state) =>
      (status: DocNetIssueStatus): DocNetIssue[] =>
        state.issues.filter((t) => t.status === status),

    /**
     * Issues matching current filters. Since filters are applied server-side,
     * this returns the loaded page as-is.
     */
    filteredIssues(state): DocNetIssue[] {
      return state.issues;
    },

    openCount(state): number {
      return state.issues.filter((t) => t.status === 'open').length;
    },

    inProgressCount(state): number {
      return state.issues.filter((t) => t.status === 'in_progress').length;
    },

    doneCount(state): number {
      return state.issues.filter((t) => t.status === 'done').length;
    },

    inReviewCount(state): number {
      return state.issues.filter((t) => t.status === 'in_review').length;
    },

    blockedCount(state): number {
      return state.issues.filter((t) => t.status === 'error').length;
    },

    /** Total open + in_progress + in_review tasks (for badge display) */
    activeIssueCount(state): number {
      return state.issues.filter(
        (t) => t.status === 'open' || t.status === 'in_progress' || t.status === 'in_review',
      ).length;
    },
  },

  actions: {
    async fetchIssues(_force?: boolean): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.issues = mockIssues;
        this.totalIssues = mockIssues.length;
        return;
      }
      this.loading = true;
      try {
        const missionStore = useDocNetMissionStore();
        // Ensure missions are loaded so we can resolve mission_name on issues
        if (missionStore.missions.length === 0) {
          await missionStore.fetchMissions(true).catch(() => {});
        }
        const skip = (this.page - 1) * this.pageSize;
        const reqFilters = { ...this.filters };
        const reqSearch = this.searchFilter;
        const reqPageSize = this.pageSize;
        const reqPage = this.page;
        const { items, total } = await issueService.getIssues({
          skip,
          limit: reqPageSize,
          status: reqFilters.status || undefined,
          priority: reqFilters.priority || undefined,
          assignee_id: reqFilters.assignee_id || undefined,
          mission_id: reqFilters.mission_id || undefined,
          q: reqSearch || undefined,
        });

        // Drop the response if the user's filters/page/search moved on while
        // we were waiting. Prevents a slow unfiltered fetch from stomping a
        // newer filtered one — the pattern that made the assignee filter
        // look broken when a late WS refetch landed after the user-clicked
        // filter call had already resolved.
        const stale =
          reqFilters.status !== this.filters.status ||
          reqFilters.priority !== this.filters.priority ||
          reqFilters.assignee_id !== this.filters.assignee_id ||
          reqFilters.mission_id !== this.filters.mission_id ||
          reqSearch !== this.searchFilter ||
          reqPage !== this.page ||
          reqPageSize !== this.pageSize;
        if (stale) {
          return;
        }
        for (const task of items) {
          if (task.mission_id && !task.mission_name) {
            const mission = missionStore.missions.find((m) => m.id === task.mission_id);
            if (mission) {
              task.mission_name = mission.name;
            }
          }
        }
        this.issues = items;
        this.totalIssues = total;
      } catch (error) {
        console.error('[DocNet] Failed to fetch issues:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async setIssuePage(page: number): Promise<void> {
      this.page = page;
      await this.fetchIssues(true);
    },

    /**
     * Fetch all open issues assigned to or reported by the current user.
     * Used to render the "My Issues" section across pages — independent of
     * the paginated main list.
     */
    async fetchMyIssues(userId: string): Promise<void> {
      if (!userId) {
        this.myIssues = [];
        return;
      }
      try {
        // Fetch assignee-filtered issues from the server. Upper-bound limit
        // keeps it cheap; most users have few open assignments.
        const { items } = await issueService.getIssues({
          assignee_id: userId,
          limit: 100,
        });
        const doneStatuses = new Set(['done', 'cancelled']);
        const assigned = items.filter((i) => !doneStatuses.has(i.status));

        // Also include issues reported by the user that aren't already in the assigned set.
        // Backend has no reporter_id filter, so skip this for now — assignee is the primary
        // "my issues" signal. If reporter_id filtering is added to the API later, merge here.
        this.myIssues = assigned;
      } catch (error) {
        console.error('[DocNet] Failed to fetch my issues:', error);
        this.myIssues = [];
      }
    },

    /**
     * Fetch all issues linked to a specific mission.
     * Used by the mission detail page — independent of the paginated main list
     * so mission sub-issues appear immediately regardless of which page the
     * user is on in /doc-net/issues.
     */
    async fetchMissionIssues(missionId: string): Promise<void> {
      if (!missionId) {
        this.missionIssues = [];
        return;
      }
      try {
        const { items } = await issueService.getIssues({
          mission_id: missionId,
          limit: 200,
        });
        this.missionIssues = items;
      } catch (error) {
        console.error('[DocNet] Failed to fetch mission issues:', error);
        this.missionIssues = [];
      }
    },

    async fetchIssue(id: string, _force?: boolean): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.currentIssue = mockIssues.find(i => i.id === id) ?? null;
        return;
      }
      this.loading = true;
      // Reset run state when loading a new issue
      this.runs = [];
      this.activeRunId = null;
      try {
        const task = await issueService.getIssueById(id);
        this.currentIssue = task ?? null;

        // Single source of truth: whenever we get a fresh issue from the
        // server, update every list that might display it. Prevents the
        // classic "list shows stale status, detail drawer shows fresh status"
        // divergence.
        if (task) {
          this._syncIssueInLists(task);
        }
      } catch (error) {
        console.error('[DocNet] Failed to fetch issue:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Silent, targeted refresh of a single issue — driven by WebSocket
     * `issue_updated` events. Unlike fetchIssue, does NOT:
     *   - toggle `loading` (would flash list spinners)
     *   - reset `runs` / `activeRunId` (would break live run streams)
     *   - replace `currentIssue` unless it already matches
     * Just syncs the fresh issue into every list it already appears in.
     */
    async refreshIssue(id: string): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) return;
      try {
        const task = await issueService.getIssueById(id);
        if (!task) return;
        this._syncIssueInLists(task);
        if (this.currentIssue?.id === id) {
          this.currentIssue = task;
        }
      } catch (error) {
        // Silent — WS-driven refreshes shouldn't surface errors to the user.
        console.debug('[DocNet] refreshIssue failed (silent):', error);
      }
    },

    /**
     * Update an issue in every list that currently holds it.
     * Called whenever we have fresh single-issue data from the server.
     */
    _syncIssueInLists(task: DocNetIssue): void {
      const updateIn = (list: DocNetIssue[]): void => {
        const idx = list.findIndex((t: DocNetIssue) => t.id === task.id);
        if (idx !== -1) list[idx] = task;
      };
      updateIn(this.issues);
      updateIn(this.missionIssues);
      updateIn(this.myIssues);
    },

    async createIssue(data: DocNetIssueCreate): Promise<DocNetIssue> {
      this.loading = true;
      try {
        const task = await issueService.createIssue(data);

        // Resolve display names from related stores
        if (task.assignee_id && !task.assignee_name) {
          const agentStore = useDocNetAgentStore();
          const profile = agentStore.getProfileById(task.assignee_id);
          if (profile) {
            task.assignee_name = profile.agent.name;
            task.assignee_type = 'agent';
          }
        }
        if (task.mission_id && !task.mission_name) {
          const missionStore = useDocNetMissionStore();
          const mission = missionStore.missions.find(
            (m) => m.id === task.mission_id,
          );
          if (mission) {
            task.mission_name = mission.name;
          }
        }

        this.issues.push(task);
        return task;
      } catch (error) {
        console.error('[DocNet] Failed to create issue:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateIssue(
      id: string,
      data: Partial<DocNetIssue>,
    ): Promise<DocNetIssue> {
      try {
        const updated = await issueService.updateIssue(id, data);
        this._syncIssueInLists(updated);
        if (this.currentIssue?.id === id) {
          this.currentIssue = updated;
        }
        return updated;
      } catch (error) {
        console.error('[DocNet] Failed to update issue:', error);
        throw error;
      }
    },

    async deleteIssue(id: string): Promise<void> {
      this.loading = true;
      try {
        await issueService.deleteIssue(id);
        this.issues = this.issues.filter((t) => t.id !== id);
        if (this.currentIssue?.id === id) {
          this.currentIssue = null;
        }
      } catch (error) {
        console.error('[DocNet] Failed to delete issue:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addComment(
      taskId: string,
      content: string,
      authorName: string,
    ): Promise<void> {
      try {
        const comment = await issueService.addComment(
          taskId,
          content,
          authorName,
        );
        const task = this.issues.find((t) => t.id === taskId);
        if (task) {
          task.comments.push(comment);
        }
        if (this.currentIssue?.id === taskId) {
          this.currentIssue.comments.push(comment);
        }
      } catch (error) {
        console.error('[DocNet] Failed to add comment:', error);
        throw error;
      }
    },

    async updateSubIssueStatus(
      taskId: string,
      subTaskId: string,
      status: DocNetIssueStatus,
    ): Promise<void> {
      try {
        const updatedSubTask = await issueService.updateSubIssue(
          taskId,
          subTaskId,
          status,
        );
        const task = this.issues.find((t) => t.id === taskId);
        if (task) {
          const subIndex = task.sub_tasks.findIndex(
            (st) => st.id === subTaskId,
          );
          if (subIndex !== -1) {
            task.sub_tasks[subIndex] = updatedSubTask;
          }
        }
        if (this.currentIssue?.id === taskId) {
          const subIndex = this.currentIssue.sub_tasks.findIndex(
            (st) => st.id === subTaskId,
          );
          if (subIndex !== -1) {
            this.currentIssue.sub_tasks[subIndex] = updatedSubTask;
          }
        }
      } catch (error) {
        console.error('[DocNet] Failed to update sub-issue status:', error);
        throw error;
      }
    },

    async fetchRuns(issueId: string): Promise<void> {
      try {
        const result = await issueService.getRuns(issueId);
        this.runs = result;
        // Sync activeRunId with any currently running run
        const runningRun = result.find(r => r.status === 'running');
        this.activeRunId = runningRun?.id ?? null;
      } catch (error) {
        console.error('[DocNet] Failed to fetch runs:', error);
      }
    },

    async triggerRun(issueId: string): Promise<string | null> {
      try {
        const result = await issueService.triggerRun(issueId);
        this.activeRunId = result.run_id;
        await this.fetchRuns(issueId);
        return result.run_id;
      } catch (error) {
        console.error('[DocNet] Failed to trigger run:', error);
        throw error;
      }
    },

    async stopRun(issueId: string, runId: string): Promise<void> {
      try {
        await issueService.stopRun(issueId, runId);
        await this.fetchRuns(issueId);
        this.activeRunId = null;
      } catch (error) {
        console.error('[DocNet] Failed to stop run:', error);
        throw error;
      }
    },

    pushRunEvent(runId: string, event: DocNetRunEvent): void {
      const run = this.runs.find(r => r.id === runId);
      if (run) {
        run.events.push(event);
      }
    },

    updateRunStatus(runId: string, status: string): void {
      const run = this.runs.find(r => r.id === runId);
      if (run) {
        run.status = status as DocNetAgentRun['status'];
        if (status !== 'running') {
          this.activeRunId = null;
        }
      }
    },

    async setFilter(key: keyof DocNetIssueFilters, value: string): Promise<void> {
      this.filters[key] = value as never;
      this.page = 1; // reset to first page when filter changes
      await this.fetchIssues(true);
    },

    async setSearchFilter(search: string): Promise<void> {
      // Caller (component) is responsible for debouncing; this method just
      // trims + dedupes + refetches. Dedupe prevents a debounced-key stream
      // of the same term from hammering the API once the user pauses.
      const trimmed = search?.trim() ?? '';
      if (trimmed === this.searchFilter) return;
      this.searchFilter = trimmed;
      this.page = 1;
      await this.fetchIssues(true);
    },

    async clearFilters(): Promise<void> {
      this.filters = {
        status: '',
        priority: '',
        type: '',
        assignee_id: '',
        mission_id: '',
      };
      this.searchFilter = '';
      this.page = 1;
      await this.fetchIssues(true);
    },
  },
});
